import { datasetSliceName, userDataSliceName } from '@/redux/constants';
import { isReadOnly } from '@/Utils/sharing';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import IntersectionObserverWrapper from '../IntersectionObserverWrapper/IntersectionObserverWrapper';
import { Button } from '../UI/Button/Button';
import TextInput from '../UI/Form/TextInput/TextInput';
import Icon from '../UI/Icon/Icon';
import CustomColumnManagerItem from './CustomColumnManagerItem';
import CustomColumnManagerSelector from './CustomColumnManagerSelector';

const CustomContextMenu = ({ topCoordinate = '', handleDeleteSelectedColumns }) => {
  return (
    <div
      className={clsx('absolute z-10 top-0 right-0 rounded w-60 border border-ui p-2 bg-white')}
      style={{ top: `${topCoordinate - 60}px` }}
    >
      <ul>
        <li>
          <Button
            variant='ghost'
            iconName='trash'
            className='!w-full !flex !justify-start !items-center'
            onClick={handleDeleteSelectedColumns}
          >
            Delete Selected Columns
          </Button>
        </li>
      </ul>
    </div>
  );
};

const CustomColumnManagerDesktopVersion = ({
  filteredColumns,
  handleColumnCheckbox,
  toggleColumnVisibility,
  numberOfVisibleColumns,
  isAllColumnsVisible,
  isContextMenuOpen,
  contextMenuTopPosition,
  handleDeleteSelectedColumns,
  query,
  agGrid,
  handleSearchValue,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleColumnSelection,
  handleDeleteColumn,
  selectedColumn,
  handleRightMouseClick,
  handleRenameColumns,
  handleDeleteVisibleColumns,
  handleDeleteHiddenColumns,
}) => {
  const { user, isViewerAnalyst } = useSelector((state) => state[userDataSliceName]);
  const { currentFile } = useSelector((state) => state[datasetSliceName]);

  const revenueBaseAccess = !!user?.userProperties?.revenueBaseAccess && isReadOnly(currentFile);

  return (
    <div data-cy='column-manager-body'>
      <div className='relative'>
        {isContextMenuOpen && (
          <CustomContextMenu
            topCoordinate={contextMenuTopPosition}
            handleDeleteSelectedColumns={handleDeleteSelectedColumns}
          />
        )}
        <div className='flex items-center justify-between mt-1 mb-2 text-sm font-semibold'>
          <span className=' text-midnight-500'>Manage Columns</span>
          <Button
            variant='ghost'
            iconName='question'
            className='!ring-0 !outline-none'
            size='small'
          />
        </div>
        <div className='relative flex items-center w-full'>
          <div className='absolute top-2 left-1 '>
            <Icon name='magnifying-glass' />
          </div>
          <TextInput
            dataCy='column-manager-search-input'
            value={query}
            onChange={handleSearchValue}
            className='!w-full mb-3 !px-6'
            placeholder='Search Columns'
          />
        </div>
        <div className='relative flex items-center justify-between w-full mb-2'>
          <CustomColumnManagerSelector
            isAllColumnsVisible={isAllColumnsVisible}
            toggleColumnVisibility={toggleColumnVisibility}
            numberOfVisibleColumns={numberOfVisibleColumns}
          />
          {isViewerAnalyst || revenueBaseAccess ? null : (
            <Button
              dataCy='column-manager-rename-button'
              color='shadow'
              variant='ghost'
              size='medium'
              onClick={handleRenameColumns}
              iconName='textbox'
            >
              Rename
            </Button>
          )}
        </div>
        <hr className='mb-2' />
        <div className='overflow-auto h-60'>
          {!filteredColumns.length ? (
            <div
              data-cy='column-manager-no-options-element'
              className='flex items-start justify-center h-full text-shadow-600'
            >
              No options
            </div>
          ) : (
            filteredColumns.map((col, index) => {
              return (
                <div
                  key={col.field}
                  className='h-8 py-1'
                  draggable
                  onDragStart={(e) => handleDragStart(e, col)}
                  onDragOver={(e) => handleDragOver(e)}
                  onDrop={(e) => handleDrop(e, col)}
                >
                  <IntersectionObserverWrapper>
                    <CustomColumnManagerItem
                      col={col}
                      agGrid={agGrid}
                      handleColumnCheckbox={handleColumnCheckbox}
                      handleColumnSelection={handleColumnSelection}
                      handleDeleteColumn={handleDeleteColumn}
                      selectedColumn={selectedColumn}
                      colIndex={index}
                      handleRightMouseClick={handleRightMouseClick}
                    />
                  </IntersectionObserverWrapper>
                </div>
              );
            })
          )}
        </div>
        <hr className='mt-3' />
      </div>
      {isViewerAnalyst || revenueBaseAccess ? null : (
        <>
          <div className='flex items-center justify-between pt-2'>
            <Button
              dataCy='column-manager-delete-visible-button'
              color='shadow'
              variant='ghost'
              size='medium'
              onClick={handleDeleteVisibleColumns}
              disabled={!numberOfVisibleColumns}
            >
              Delete Visible
            </Button>
            <Button
              dataCy='column-manager-delete-hidden-button'
              color='shadow'
              variant='ghost'
              size='medium'
              onClick={handleDeleteHiddenColumns}
              disabled={isAllColumnsVisible}
            >
              Delete Hidden
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default CustomColumnManagerDesktopVersion;
